import React, { Component } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import {
  colors,
  copyToClipboard,
  downloadMap,
  downloadSVG,
  handleClickOutsideModal,
} from "../helpers";
import { downloadPDF } from "../pdfDownload";
import { Card } from "../styles";
import { Code, Copy, DownloadIcon, Link } from "./icons";

const ModalContainer = styled.div`
  position: fixed;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  background-color: rgba(0, 0, 0, 0.6);
`;

const ModalCard = styled(Card)`
  width: 550px;
  display: flex;
  flex-direction: column;
  text-align: center;
  position: relative;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.9);
  textarea {
    color: gray;
    font-family: Monospace;
  }
`;

const TooltipMsg = styled.div`
  width: 100%;
  color: black;
  font-size: 14px;
  text-align: center;
  font-style: italic;
`;

const defaultEmbedText = (url) =>
  `<iframe id="vectorsurvEmbedded" title="Embedded VectorSurv Visualizations" width="1000" height="700" allow="geolocation" src="${url}"> </iframe>`;

const CloseButton = styled.div`
  position: absolute;
  top: 8px;
  right: 15px;
  color: ${colors["vectorsurv-navy"]};
  cursor: pointer;
`;

const MapSettings = styled.div`
  color: ${colors["vectorsurv-navy"]};
  cursor: pointer;
`;

const ButtonWithIcon = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 150px;
  min-height: 50px;
  padding: 5px;
  background: ${(props) => (props.color ? props.color : colors["light-blue"])};
  color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  cursor: pointer;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 10px 0px;
`;

const CitationContainer = styled.div`
  margin: 12px auto;
  padding: 10px;
  border: 1px solid ${colors["gray"]};
  border-radius: 6px;
  width: 90%;
  background: #fafafa;
  text-align: left;
  position: relative;
`;

const CitationLabel = styled.div`
  font-size: 13px;
  font-style: italic;
  margin-bottom: 6px;
`;

const CitationText = styled.pre`
  margin: 0;
  font-family: monospace;
  font-size: 13px;
  white-space: pre-wrap;
`;

const CopyCitation = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
`;

class ShareModal extends Component {
  constructor(props) {
    super(props);

    const cleanUrl = this.props.url;

    let embedUrl = this.props.url;
    if (this.props.currentMap === "agency") {
      // special case for agency map embed
      const hasEmbedded = /(?:[?&])embedded=/.test(cleanUrl);
      if (!hasEmbedded) {
        embedUrl =
          cleanUrl +
          (cleanUrl.indexOf("?") === -1 ? "?" : "&") +
          "embedded=true";
      }
    }

    // Dynamic citation text
    const now = new Date();
    const year = now.getFullYear();
    const accessed = now.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    this.state = {
      embedCode: defaultEmbedText(embedUrl),
      citation: `VectorSurv Development Team. ${year}. VectorSurv Maps. https://maps.vectorsurv.org (accessed ${accessed}). Map data ©Mapbox, ©OpenStreetMap`,
      message: null,
      url: cleanUrl,
    };
  }

  componentDidMount() {
    document.addEventListener("keydown", this.escToClose, true);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.escToClose, true);
  }

  escToClose = (e) => {
    if (e.keyCode === 27) {
      this.props.closeModal();
    }
  };

  copyURL = () => {
    copyToClipboard(this.state.url);
    this.setState({ message: "URL copied to your clipboard 😃" });
  };

  copyEmbed = () => {
    copyToClipboard(this.state.embedCode);
    this.setState({ message: "Code copied to your clipboard 👨🏻‍💻" });
  };

  copyCitation = () => {
    copyToClipboard(this.state.citation);
    this.setState({ message: "Attribution copied to your clipboard 📋" });
  };

  changeEmbedCode = (e) => {
    this.setState({ embedCode: e.target.value });
  };

  render() {
    const { embedCode, message, citation } = this.state;
    const {
      closeModal,
      currentMap,
      downloadInfo,
      map,
      openStyleModal,
      svgOptions,
    } = this.props;
    const chartEl = document.querySelector("#chartContainer svg");
    const urlMap = document.location.pathname.replace("/", "").replace("/", "");

    const showPdfButton = map && currentMap === urlMap && currentMap === "risk";

    // new style to ensure all buttons easy to see
    const modalStyle = showPdfButton
      ? { height: "550px" }
      : { height: "460px" };

    return (
      <ModalContainer
        onClick={(e) => handleClickOutsideModal(e, this.props.closeModal)}
      >
        <ModalCard className={"modal-card"} style={modalStyle}>
          <CloseButton onClick={closeModal}>Close</CloseButton>
          <h1>Share VectorSurv visualizations</h1>

          {message && <TooltipMsg>{message}</TooltipMsg>}

          <Row>
            <ButtonWithIcon color={colors["green"]} onClick={this.copyURL}>
              <Link color="white" />
              Copy link to this configured page
            </ButtonWithIcon>
          </Row>

          <Row>
            {map && (
              <ButtonWithIcon
                color={colors["light-blue"]}
                onClick={() => downloadMap(map)}
              >
                <DownloadIcon color={"white"} />
                Download Map
              </ButtonWithIcon>
            )}
            {chartEl && (
              <ButtonWithIcon
                color={colors["pink"]}
                onClick={() => downloadSVG(svgOptions)}
              >
                <DownloadIcon color={"white"} />
                Download Chart
              </ButtonWithIcon>
            )}
          </Row>

          <Row>
            <textarea
              id="embedCode"
              value={embedCode}
              onChange={this.changeEmbedCode}
              style={{ width: "150px", minHeight: "50px" }}
            />
            <ButtonWithIcon color={colors["gray"]} onClick={this.copyEmbed}>
              <Code color="white" />
              Copy code to embed
            </ButtonWithIcon>
          </Row>

          {showPdfButton && (
            <Row>
              <ButtonWithIcon
                color={"salmon"}
                onClick={() =>
                  downloadPDF(downloadInfo[currentMap], map, currentMap)
                }
              >
                <Link color="white" />
                PDF DOWNLOAD BUTTON <sub>BETA</sub>
              </ButtonWithIcon>
            </Row>
          )}

          <CitationContainer>
            <CitationLabel>Please attribute:</CitationLabel>
            <CitationText>{citation}</CitationText>
            <CopyCitation onClick={this.copyCitation}>
              <Copy color={colors["gray"]} />
            </CopyCitation>
          </CitationContainer>

          <Row>
            <MapSettings onClick={openStyleModal}>Change Map Style</MapSettings>
          </Row>
        </ModalCard>
      </ModalContainer>
    );
  }
}

ShareModal.propTypes = {
  url: PropTypes.string.isRequired,
  map: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  openStyleModal: PropTypes.func.isRequired,
  downloadInfo: PropTypes.object.isRequired,
  currentMap: PropTypes.string,
  svgOptions: PropTypes.object,
};

export default ShareModal;
